import { Auth0Service, getToken as getTok } from '@/auth/auth0'
import { finsquid } from '@/integrations/finsquid'
import type { FindexAsset } from '@/types/models/asset'
import type { FindexInvestment, FullAsset, FullInvestment, IAssetAdmin, ICommunication, ICompanyProfile, ITicker, IUpdateAssetAdmin, IValue, UserDetails } from 'findef'
import { getErrorMessage } from '@/utils/error'
import { Auth0Plugin } from '@auth0/auth0-vue'
import {
  EAssetSource,
  EAssetSubtype,
  EAssetType,
  type FindexEvent,
  type IAsset,
  type IInvestment,
  type ILedger,
  type ISavedDocument,
  type IUser
} from 'findef'
import type {
  IntegrationImport,
  IntegrationImportWithProvider,
  FindexNewsResponse,
  IAssetPreference,
  IAssetRelation,
  IPortfolioSnapshot,
  IntegrationProvider,
  InvestmentTransaction,
  Portfolio,
  PortfolioTrends,
  ProviderSession,
  TimelinePerformanceItem,
  TrendsGraph,
  INotification,
  DocumentId,
  ENotificationStatus,
  CreateUserAccountRequest,
  EAuthenticationMethod,
  IInviteDocument,
  INewsItem
} from 'findef'
import type { IntegrationPerformance } from 'findef'
import * as fns from 'date-fns'
import { isDate } from '@/utils/is'
import { doAsyncWithProgress, type DoAsyncWithProgressCallback } from '@/utils/async'
import type { FindexUser } from '@/types/models/user'
import type { DateInterval } from '@/types/date'
import { getFindexSessionId } from '../auth/hooks/useSession'
import { urlEncode } from '@/utils/url'
import { env } from "@/utils/env";

export const BASE_URL: string = env.VITE_APP_SERVICE_IR

export type HandshakeResponse = { user?: Partial<ISavedDocument<IUser>>; message?: string }

// TODO: put in definitions repo because it's being used in the backend too.
export interface IUpdateShareholder {
  firstname?: string
  lastname?: string
  email?: string
  investments?: Partial<IInvestment>[]
  shareholderType?: string
  ownedBy?: {
    name?: string
    organizationNbr?: string
  }
}

class IRService {
  auth: Auth0Plugin
  token?: string
  user?: ISavedDocument<IUser, string>

  constructor(auth: Auth0Plugin) {
    this.auth = auth
  }

  async getToken(): Promise<string> {
    const token = await getTok();
    if (!token) throw new Error('Could not get token');
    return token;
  }

  async getHeaders(requiresAuth: boolean = true): Promise<HeadersInit> {
    if (requiresAuth && !this.auth.isAuthenticated) {
      console.warn('Not authenticated.')
      return {}
    }

    const token = requiresAuth ? await this.getToken() : undefined;
    const sessionId = requiresAuth ? getFindexSessionId() : undefined;

    return {
      ...(token ? {
        Authorization: 'Bearer ' + token,
      } : {}),
      'Content-Type': 'application/json',
      ...(sessionId ? {
        'X-findexSessionId': sessionId,
        'access-control-expose-headers': 'X-findexSessionId'
      } : {})
    }
  }

  async get<T = any>(
    endpoint: string,
    userHeaders: Record<string, string> = {},
    requiresAuth: boolean = true
  ) {
    const headers = { ...(userHeaders || {}), ...(await this.getHeaders(requiresAuth)) }
    const resource = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const resp = await fetch(`${BASE_URL}${resource}`, { headers })
    if (resp.status >= 300) throw new Error(await resp.text())
    const data = await resp.json()
    return data as T
  }

  async delete<T = any, PayloadType = unknown>(endpoint: string, payload?: PayloadType) {
    const headers = await this.getHeaders()
    const resource = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const resp = await fetch(`${BASE_URL}${resource}`, {
      headers,
      method: 'DELETE',
      body: payload === undefined ? undefined : JSON.stringify(payload)
    })
    if (resp.status >= 300) throw new Error(await resp.text())
    const data = await resp.json()
    return data as T
  }

  async patch<T = any, PayloadType = {}>(
    endpoint: string,
    payload: PayloadType,
    userHeaders: Record<string, string> = {}
  ) {
    const headers = { ...(userHeaders || {}), ...(await this.getHeaders()) }
    const resource = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const resp = await fetch(`${BASE_URL}${resource}`, {
      headers,
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
    if (resp.status >= 300) throw new Error(await resp.text())
    const data = await resp.json()
    return data as T
  }

  async post<T = any, PayloadType = {}>(
    endpoint: string,
    payload: PayloadType,
    userHeaders: Record<string, string> = {},
    requiresAuth: boolean = true
  ) {
    const headers = { ...(userHeaders || {}), ...(await this.getHeaders(requiresAuth)) }
    const resource = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const resp = await fetch(`${BASE_URL}${resource}`, {
      headers,
      method: 'POST',
      body: JSON.stringify(payload)
    })
    if (resp.status >= 300) throw new Error(await resp.text())
    const data = await resp.json()
    return data as T
  }

  async put<T = any, PayloadType = {}>(endpoint: string, payload: PayloadType) {
    const headers = await this.getHeaders()
    const resource = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const resp = await fetch(`${BASE_URL}${resource}`, {
      headers,
      method: 'PUT',
      body: JSON.stringify(payload)
    })
    if (resp.status >= 300) throw new Error(await resp.text())

    const text = await resp.text()
    if (text) {
      return JSON.parse(text) as T
    }
    return {} as T
  }

  async handshake(onError?: (message?: string) => void): Promise<HandshakeResponse | undefined> {
    console.log('-- handshake --')
    try {
      const token = await this.getToken()
      if (!token) throw new Error('Missing token')
      console.log(`token found, let's handshake.`)
      const resp = await this.get<HandshakeResponse>(`/handshake`)
      if (!resp) throw new Error('Handshake failed for unknown reason.')
      if (!resp.user) throw new Error(resp.message || 'Unknown error')
      const user = resp.user
      console.log(`Handshake success, welcome ${user.email || user._id}`)
      return resp
    } catch (e) {
      if (onError) {
        onError(getErrorMessage(e))
      }
      console.error('handshake failed.')
      console.error(e)
    }
  }

  async authorize() {
    await this.get('jwt')
  }

  //////////////////////////

  async getUser(email: string): Promise<ISavedDocument<IUser, string>> {
    //await this.authorize();
    //if (this.user) return this.user;
    this.user = await this.get(`/user/${email}?full=true`)
    return this.user
  }

  async whoami(token?: string): Promise<ISavedDocument<IUser, string>> {
    // Authorization header instead of ?token= query param: the legacy query
    // path bypassed signature verification, and the API's JWT middleware
    // reads only the header. requiresAuth=false when an explicit token is
    // passed so the default auth headers don't override it. (DEV-4464)
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    return await this.get(`/user/me`, headers, !token);
  }

  // deprecated
  async getUserInvestments(userIdOrEmail: string): Promise<FindexInvestment[]> {
    try {
      if (!userIdOrEmail) throw new Error('Missing `userIdOrEmail`')
      return (await this.get<FindexInvestment[]>(`/user/${userIdOrEmail}/investments`)).filter(
        (inv) => !!inv.asset
      )
    } catch (e) {
      console.error(e)
      return []
    }
  }

  async getInvestments(query: {
    offset?: number,
    limit?: number,
    queryString?: string,
    assetType?: EAssetType,
    listed?: boolean,
    sort?: Record<string, number>
  } = {}): Promise<FindexInvestment[]> {
    const params = urlEncode({
      ...query,
      sort: JSON.stringify(query.sort || {})
    });
    return await this.get<FindexInvestment[]>(`/user/investments?${params}`)
  }

  async getInvestmentsCount(query: {
    assetType?: EAssetType,
    listed?: boolean
  } = {}) {
    const params = urlEncode(query);
    return await this.get<{ count: number }>(`/user/investments/count?${params}`)
  }

  async getUserIntegrationImports(): Promise<IntegrationImportWithProvider[]> {
    return await this.get<IntegrationImportWithProvider[]>(`/user/imports`)
  }

  async getUserIntegrationImportByProviderId(
    providerId: number
  ): Promise<IntegrationImportWithProvider> {
    return await this.get(`/user/imports/${providerId}`)
  }

  async updateUserIntegrationImportByProviderId(
    providerId: number,
    data: Partial<IntegrationImport>
  ): Promise<ISavedDocument<IUser>> {
    return await this.patch(`/user/imports/${providerId}`, data)
  }

  async updateUserIntegrationImportSessionByProviderId(
    providerId: number,
    sid: string
  ): Promise<{ alive: boolean }> {
    return await this.patch(`/user/imports/${providerId}/updateSession`, { sid }, { sid })
  }

  async deleteUserIntegrationImportByProviderId(
    providerId: number
  ): Promise<ISavedDocument<IUser>> {
    return await this.delete(`/user/imports/${providerId}`)
  }

  async updateAssetLedger(
    assetId: string,
    ledger: ILedger
  ): Promise<ISavedDocument<IAsset, string>> {
    return await this.patch<ISavedDocument<IAsset, string>, ILedger>(
      `/asset/${assetId}/updateLedger`,
      ledger
    )
  }

  async updateUserDefinedAssetRelations(
    userIdOrEmail: string,
    relations: IAssetRelation
  ): Promise<IAssetRelation> {
    return await this.patch<IAssetRelation>(
      `/user/${userIdOrEmail}/definitions/assetRelations/update`,
      relations
    )
  }

  //async setParentAsset(childAssetId: string, parentAssetId: string): Promise<FindexAsset> {
  //  return await this.patch<FindexAsset>(`/asset/${childAssetId}/setParent`, { parentId: parentAssetId });
  //}

  async deleteInvestment(investmentId: string) {
    return await this.delete(`/investment/${investmentId}`)
  }

  async deleteInvestments(ids: string[]): Promise<{
    deleted: string[]
    failed: Array<{ id: string; reason: 'not_found' | 'forbidden' | 'error' }>
  }> {
    return await this.delete('/investment', { ids })
  }

  async getInvestmentById(investmentId: string): Promise<FindexInvestment> {
    return await this.get(`/investment/${investmentId}`)
  }

  async updatePortfolioStatistics(): Promise<Portfolio> {
    // will re-compute portfolio
    return await this.patch<Portfolio>(`/user/graphs/portfolio`, {})
  }

  async getPortfolioStatistics(): Promise<Omit<Portfolio, 'diversification'>> {
    // this one will also re-compute, it's actually the same as the one above.
    return await this.get<Portfolio>(`/user/graphs/portfolio`)
  }

  async getPortfolioTrends(interval?: fns.Interval): Promise<PortfolioTrends> {
    const a = interval && isDate(interval.start) ? interval.start.getTime() : ''
    const b = interval && isDate(interval.end) ? interval.end.getTime() : ''

    return await this.get<PortfolioTrends>(
      `/user/graphs/trends` + (interval ? `?start=${a.toString()}&end=${b.toString()}` : ``)
    )
  }

  async getTrendsGraph(interval?: DateInterval, fmt?: string): Promise<TrendsGraph> {
    const a = interval && isDate(interval.start) ? interval.start.getTime() : ''
    const b = interval && isDate(interval.end) ? interval.end.getTime() : ''

    const params = new URLSearchParams({
      start: a.toString(),
      end: b.toString()
    })

    if (fmt) {
      params.set('fmt', fmt)
    }

    return await this.get<TrendsGraph>(`/user/graphs/tmp?` + params.toString())
  }

  async getProfitGraph(interval?: DateInterval, assetType?: EAssetType, listed?: boolean): Promise<TrendsGraph> {
    const params = new URLSearchParams({})

    if (interval?.start) params.set('start', interval.start.getTime() + '')
    if (interval?.end) params.set('end', interval.end.getTime() + '')
    if (assetType) params.set('assetType', assetType)
    if (listed !== undefined) params.set('listed', listed.toString() + '')

    return await this.get<TrendsGraph>(`/user/graphs/profit?${params.toString()}`)
  }

  async getTransactions(): Promise<InvestmentTransaction[]> {
    const sid = finsquid.getSID() || ''
    return await this.get<InvestmentTransaction[]>(`/user/transactions`, { sid: sid })
  }

  async syncTransactions(): Promise<InvestmentTransaction[]> {
    const sid = finsquid.getSID() || ''
    return await this.get<InvestmentTransaction[]>(`/user/transactions/sync`, { sid: sid })
  }

  async removeShareholderFromAsset(shareholderIdOrEmail: string, assetId: string): Promise<void> {
    return await this.delete(`/asset/${assetId}/shareholder/${shareholderIdOrEmail}`)
  }

  async updateInvestment(investmentId: string, investment: Partial<IInvestment>) {
    const makeValue = (val?: Partial<IValue>): IValue => ({
      ...(val || {}),
      value: val?.value || 0,
      type: val?.type || 'SEK',
      percentage: val?.percentage || 0,
      time: val?.time || new Date()
    })

    return await this.patch(`/investment/${investmentId}`, {
      ...investment,
      currentValue: makeValue(investment.currentValue),
      invested: makeValue(investment.invested)
    })
  }

  async getFinsquidToken(): Promise<string> {
    const resp = await this.get<{ token: string }>('/finsquid/token')
    return resp.token
  }

  async importFinsquidPositions(providerIds: number[]): Promise<{ count: number }> {
    const sid = finsquid.getSID() || ''
    return await this.post<{ count: number }>(
      '/finsquid/positions/import',
      { providerIds },
      { sid: sid }
    )
  }

  async importFinsquidPositionsV2(
    data: IntegrationImport
  ): Promise<{ count: number; errors?: string[] }> {
    const sid = finsquid.getSID() || ''
    return await this.post<{ count: number; errors?: string[] }>(
      '/finsquid/positions/importv2',
      data,
      { sid: sid }
    )
  }

  async syncPositions(): Promise<any> {
    const sid = finsquid.getSID() || ''
    return await this.post<{ token: string }>(
      '/finsquid/positions/import',
      { providerIds: [] }, // if we send an empty array, it will use previous providers and perform an update
      { sid: sid }
    )
  }

  async addChildToAsset(assetId: string, childId: string): Promise<void> {
    return await this.put(`/asset/${assetId}/child`, { childId })
  }

  async setAssetParent(assetId: string, parentId: string): Promise<void> {
    return await this.patch(`/asset/${assetId}/parent`, { parentId })
  }

  async removeAssetParent(assetId: string): Promise<void> {
    return await this.delete(`/asset/${assetId}/parent`)
  }

  async addChildToInvestment(investmentId: string, childId: string): Promise<void> {
    return await this.put(`/investment/${investmentId}/child`, { childId })
  }

  async setInvestmentParent(investmentId: string, parentId: string): Promise<void> {
    return await this.patch(`/investment/${investmentId}/parent`, { parentId })
  }

  async removeInvestmentParent(investmentId: string): Promise<void> {
    return await this.delete(`/investment/${investmentId}/parent`)
  }

  async createAsset(
    asset: Omit<Partial<IAsset | FullAsset>, 'createdAt' | 'updatedAt' | 'ticker'> & { ticker?: ITicker }
  ): Promise<ISavedDocument<IAsset>> {
    return await this.post<ISavedDocument<IAsset>>(`/asset`, asset)
  }

  async createUnlistedAsset(
    asset: Omit<IAsset, 'createdAt' | 'updatedAt'>
  ): Promise<ISavedDocument<IAsset>> {
    return await this.createAsset({
      ...asset,
      type: EAssetType.EQUITY,
      source: EAssetSource.MANUAL,
      subtypes: [EAssetSubtype.STOCK]
    })
  }

  async createListedAsset(
    asset: Omit<IAsset, 'createdAt' | 'updatedAt'>,
    ticker: ITicker
  ): Promise<ISavedDocument<IAsset>> {
    return await this.createAsset({
      ...asset,
      type: EAssetType.EQUITY,
      source: EAssetSource.AUTOMATIC,
      subtypes: [EAssetSubtype.STOCK],
      ticker
    })
  }

  async createRealEstateAsset(
    asset: Omit<IAsset, 'createdAt' | 'updatedAt'>
  ): Promise<ISavedDocument<IAsset>> {
    return await this.createAsset({
      ...asset,
      type: EAssetType.REAL_ESTATE,
      source: EAssetSource.MANUAL
    })
  }

  async createAlternativeAsset(
    asset: Omit<IAsset, 'createdAt' | 'updatedAt'>
  ): Promise<ISavedDocument<IAsset>> {
    return await this.createAsset({
      ...asset,
      type: EAssetType.ALTERNATIVE,
      source: EAssetSource.MANUAL
    })
  }

  async investInAsset(
    assetId: string,
    investment: Omit<FindexInvestment, 'automatic' | 'asset' | '_id'>
  ): Promise<FindexInvestment> {
    return await this.post(`/user/invest`, { assetId, investment })
  }

  async getPerformance(): Promise<IntegrationPerformance[]> {
    const sid = finsquid.getSID(true)
    if (!sid) return []

    const perf = await this.get<IntegrationPerformance[]>(`/user/graphs/performance`, { sid })
    return perf.filter((p) => !!p)
  }

  async syncPerformance(): Promise<IntegrationPerformance[]> {
    const sid = finsquid.getSID(true)
    if (!sid) return []

    const perf = await this.get<IntegrationPerformance[]>(`/user/graphs/performance/sync`, { sid })
    return perf.filter((p) => !!p)
  }

  async syncAll(callback?: DoAsyncWithProgressCallback): Promise<any> {
    return await doAsyncWithProgress<any>(
      [
        { name: 'syncPositions', fun: async () => await this.syncPositions(), delay: 1000 },
        { name: 'syncTransactions', fun: async () => await this.syncTransactions(), delay: 1000 },
        { name: 'syncPerformance', fun: async () => await this.syncPerformance(), delay: 3000 }
      ],
      callback
    )
  }

  async updateAsset(
    assetId: string,
    update: Partial<IAsset & Partial<{ ledger: Partial<ILedger> }>>
  ): Promise<FindexAsset> {
    return await this.patch(`/asset/${assetId}/edit`, update)
  }

  async editInvestment(
    investmentId: string,
    update: Partial<Omit<FindexInvestment, 'asset'> & { asset: Partial<IAsset & Partial<{ ledger: Partial<ILedger> }>> }> | Partial<FullInvestment>
  ): Promise<FindexAsset> {
    return await this.patch(`/investment/${investmentId}/edit`, update)
  }

  async createEvent(event: Partial<FindexEvent>): Promise<ISavedDocument<FindexEvent>> {
    return await this.post<ISavedDocument<FindexEvent>>(`/event`, event)
  }

  async queryNews(
    page: number = 0,
    limit: number = 0,
    filter?: Partial<IAsset>,
    queryString?: string
  ): Promise<FindexNewsResponse> {
    const params = new URLSearchParams({
      offset: (page || 0).toString(),
      perPage: (limit || 0).toString(),
      filter: JSON.stringify(filter || {}),
      queryString: queryString || ''
    }).toString()
    return await this.get<FindexNewsResponse>(`/news?${params}`)
  }

  async queryNewsV2(
    skip: number = 0,
    limit: number = 16,
    filter?: Partial<IAsset>,
    queryString?: string
  ): Promise<INewsItem[]> {
    const params = urlEncode({
      skip: (skip || 0).toString(),
      limit: (limit || 0).toString(),
      queryString: queryString || '',
      assetType: filter?.type,
      listed: filter?.listed
    });
    return await this.get(`/news/v2?${params}`)
  }

  async getNewsCount(filter?: Partial<IAsset>, queryString?: string) {
    const params = urlEncode({
      filter: JSON.stringify(filter || {}),
      queryString: queryString || '',
    })
    return await this.get(`/news/count?${params}`)
  }

  async generateNews(): Promise<void> {
    return await this.patch(`/news/generate`, {})
  }

  async getNewsByAsset(assetId: string, limit?: number) {
    const params = urlEncode({
      limit: limit
    })
    return await this.get<INewsItem[]>(`/news/asset/${assetId}?${params}`)
  }

  async getCompanyProfile(assetId: string): Promise<any> {
    return await this.get<ICompanyProfile>((`/asset/${assetId}/profile`));
  }

  async getAssetById(assetId: string): Promise<FindexAsset> {
    return await this.get(`/asset/${assetId}`)
  }

  async getAssetLogo(assetId: string): Promise<{ src: string }> {
    return await this.get<{ src: string }>(`/asset/${assetId}/logo`)
  }

  async updateAssetLogoBase64(assetId: string, base64: string): Promise<IAssetPreference> {
    return await this.patch<IAssetPreference>(`/asset/${assetId}/logo`, { src: base64 })
  }

  async deleteAssetLogo(assetId: string): Promise<any> {
    return await this.delete<{ src: string }>(`/asset/${assetId}/logo`)
  }

  async addAssetAdmin(data: IUpdateAssetAdmin): Promise<ISavedDocument<IAssetAdmin>> {
    return await this.post(`/assetAdmin`, data)
  }

  async editAssetAdmin(id: DocumentId, data: IUpdateAssetAdmin): Promise<ISavedDocument<IAssetAdmin>> {
    return await this.put(`/assetAdmin/${id}`, data)
  }

  async deleteAssetAdmin(id: DocumentId): Promise<void> {
    return await this.delete(`/assetAdmin/${id}`)
  }

  async getAllAssetPreferences(): Promise<IAssetPreference[]> {
    return await this.get<IAssetPreference[]>(`/user/assetPreference`)
  }

  async getPerformanceTimeline(interval?: {
    start?: Date
    end?: Date
  }): Promise<TimelinePerformanceItem[]> {
    const start = (interval?.start || new Date('1999-01-01')).toJSON()
    const end = (interval?.end || new Date()).toJSON()
    const resp = await this.get<TimelinePerformanceItem[]>(
      `/user/graphs/trends/graph?start=${start}&end=${end}`
    )
    return resp
  }

  async getPortfolioSnapshots(): Promise<IPortfolioSnapshot[]> {
    return await this.get<IPortfolioSnapshot[]>(`/user/snapshots`)
  }

  async generatePortfolioSnapshot(): Promise<IPortfolioSnapshot> {
    return await this.post(`/user/snapshots`, {})
  }

  async deletePortfolioSnapshots(): Promise<void> {
    return await this.delete(`/user/snapshots`)
  }

  async deletePortfolioSnapshot(snapshotId: string): Promise<void> {
    return await this.delete(`/user/snapshots/${snapshotId}`)
  }

  async syncProviderStatuses(
    providerSesssion?: Omit<ProviderSession, 'provider'> & {
      provider?: Partial<IntegrationProvider>
    }
  ): Promise<FindexUser> {
    return await this.post<FindexUser>(`/user/providers/sync`, providerSesssion || {})
  }

  async updateUserProfile(data: Partial<IUser>): Promise<ISavedDocument<IUser>> {
    return await this.patch(`/user/updateProfile`, data)
  }

  async getNotifications(): Promise<ISavedDocument<INotification[]>> {
    return await this.get(`/user/notifications`)
  }

  async updateNotificationStatus(
    notificationId: DocumentId,
    status: ENotificationStatus
  ): Promise<ISavedDocument<INotification>> {
    return await this.patch(`/user/notifications/${notificationId}/updateStatus`, { status })
  }

  async userExists(idOrEmail: string): Promise<{
    db: boolean
    auth0: null | { id: string }
  }> {
    return await this.get(`/user/${idOrEmail}/exists`, {}, false)
  }

  async createUserAccount(data: CreateUserAccountRequest): Promise<ISavedDocument<IUser>> {
    return await this.post(`/user/createAccount`, data, {}, false)
  }

  async getUserAuthenticationMethod(
    idOrEmail: string
  ): Promise<{ authenticationMethod: EAuthenticationMethod }> {
    return await this.get(`/user/${idOrEmail}/authenticationMethod`, {}, false)
  }

  async getInviteById(inviteId: string): Promise<ISavedDocument<IInviteDocument>> {
    return await this.get(`/invite/${inviteId}`, {}, false)
  }

  async googleLogin(token: string): Promise<{ sessionId: string; user: ISavedDocument<IUser> }> {
    return await this.post(`/user/auth/google/login`, { token }, {}, false)
  }

  async googleRegister(data: {
    token: string
    agreeTermsDate: string
    newsLetter?: boolean
    country?: string
    betaCode?: string
    inviteId?: string
  }): Promise<{ sessionId: string; user: ISavedDocument<IUser> }> {
    return await this.post(`/user/auth/google/register`, data, {}, false)
  }

  async getInviteByBetaCode(betaCode: string): Promise<ISavedDocument<IInviteDocument>> {
    return await this.get(`/invite/byBetaCode/${betaCode}`, {}, false)
  }

  async getUserDetails(): Promise<UserDetails> {
    return await this.get(`/user/details`);
  }

  async sendEmailVerification(): Promise<{ status: string }> {
    return await this.post(`/user/sendEmailVerification`, {});
  }

  async queryTickers(
    limit: number = 16,
    queryString?: string
  ): Promise<ISavedDocument<ITicker>[]> {
    const params = urlEncode({
      limit: (limit || 0).toString(),
      queryString: queryString || '',
    });
    return await this.get(`/ticker?${params}`)
  }

  async createIRCommunication(data: ICommunication): Promise<ISavedDocument<ICommunication>> {
    return await this.post(`/communication`, data);
  }

  async deleteIRCommunication(communicationId: string): Promise<void> {
    return await this.delete(`/communication/${communicationId}`)
  }

  async editIRCommunication(communicationId: string, data: ICommunication): Promise<ISavedDocument<ICommunication>> {
    return await this.put(`/communication/${communicationId}`, data)
  }

  async publishIRCommunication(communicationId: string): Promise<void> {
    await this.post(`/communication/${communicationId}/publish`, {})
  }

  async getIRCommunicationsByAsset(assetId: string, options?: {
    offset?: number,
    limit?: number,
    sort?: Record<string, number>,
    includeArchived?: boolean
  }) {
    console.log({ sort: options?.sort })
    const params = options ? urlEncode({
      ...options,
      ...(options.sort ? {
        sort: JSON.stringify(options.sort)
      } : {})
    }) : ''
    return await this.get(`/communication/asset/${assetId}?${params}`)
  }

  async getIRCommunicationsByAssetCount(assetId: string, options?: { includeArchived?: boolean }) {
    const params = options ? urlEncode(options) : ''
    return await this.get(`/communication/asset/${assetId}/count${params ? `?${params}` : ''}`)
  }

  async getIRCommunicationById(communicationId: string): Promise<ISavedDocument<ICommunication>> {
    return await this.get(`/communication/${communicationId}`)
  }

  async getIRCommunicationsByInvestment(investmentId: string): Promise<ISavedDocument<ICommunication>[]> {
    return await this.get(`/communication/investment/${investmentId}`)
  }

  async getIRCommunicationsByInvestmentCount(investmentId: string): Promise<number> {
    return await this.get(`/communication/investment/${investmentId}/count`)
  }

  async updateCommunicationReadBy(communicationId: string): Promise<void> {
    return await this.put(`/communication/${communicationId}/read`, {})
  }

  async sendIRCommunicationTest(communicationId: string, email: string): Promise<void> {
    return await this.post(`/communication/${communicationId}/sendTest`, { email })
  }

  async sendIRCommunicationDraftTest(communication: ISavedDocument<ICommunication>, email: string): Promise<void> {
    return await this.post(`/communication/sendDraftTest`, {
      draft: communication,
      email,
      bannerColor: (communication as any)?.bannerColor
    })
  }

  async getIRCommunicationsSentCountByAsset(assetId: string): Promise<number> {
    return await this.get(`/communication/asset/${assetId}/sent`)
  }

  async checkDirectoryHasDocuments(assetIds: string[]): Promise<Array<{ assetId: string; hasDocuments: boolean }>> {
    return await this.post<Array<{ assetId: string; hasDocuments: boolean }>>(`/asset/directory/hasDocuments`, { assetIds })
  }

  async deleteAssetDirectory(assetId: string): Promise<void> {
    return await this.delete(`/asset/${assetId}/directory`)
  }

  async deleteAssetDirectories(ids: string[]): Promise<{
    deleted: string[]
    failed: Array<{ id: string; reason: 'not_found' | 'forbidden' | 'error' }>
  }> {
    return await this.delete('/asset/directory', { ids })
  }

}

const IRClient = new IRService(Auth0Service)

export default IRClient
